import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import { LoadingPage } from '../../components/Loading';
import './Member.css';

const Profile = () => {
  const { updateUser } = useAuth();
  const { success, error: toastError } = useToast();
  const { formatDate } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    chinese_name: '',
    english_name: '',
    email: '',
    phone_number: '',
    gender: '',
    birth_date: '',
    address: '',
    district: '',
    occupation: '',
    organization: '',
    id_card_number: '',
  });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getProfile();
      const data = response.data;
      const mp = data.profile;
      setProfile(data);
      
      setFormData({
        chinese_name: mp?.chinese_name || '',
        english_name: mp?.english_name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
        gender: '',
        birth_date: mp?.birth_year ? String(mp.birth_year) : '',
        address: '',
        district: mp?.district || '',
        occupation: '',
        organization: '',
        id_card_number: mp?.id_card_number || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toastError('無法加載個人資料');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.chinese_name.trim()) {
      toastError('請輸入中文姓名');
      return;
    }
    if (!formData.email.trim()) {
      toastError('請輸入電郵地址');
      return;
    }
    if (!formData.phone_number.trim()) {
      toastError('請輸入電話號碼');
      return;
    }

    try {
      setSaving(true);
      const response = await authAPI.updateProfile(formData);
      setProfile(response.data);
      updateUser(response.data);
      setEditing(false);
      success('個人資料已更新');
    } catch (error) {
      console.error('Failed to update profile:', error);
      if (error.response?.data) {
        const errors = error.response.data;
        const errorMessage = Object.values(errors).flat().join(', ');
        toastError(errorMessage || '更新失敗');
      } else {
        toastError('無法更新個人資料');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      const mp = profile.profile;
      setFormData({
        chinese_name: mp?.chinese_name || '',
        english_name: mp?.english_name || '',
        email: profile.email || '',
        phone_number: profile.phone_number || '',
        gender: '',
        birth_date: mp?.birth_year ? String(mp.birth_year) : '',
        address: '',
        district: mp?.district || '',
        occupation: '',
        organization: '',
        id_card_number: mp?.id_card_number || '',
      });
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditing(false);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toastError('請選擇圖片文件');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toastError('圖片大小不能超過 5MB');
      return;
    }

    setPhotoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('photo', photoFile);

      const response = await authAPI.updateProfile(formData);
      setProfile(response.data);
      updateUser(response.data);
      setPhotoFile(null);
      setPhotoPreview(null);
      success('頭像已更新');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toastError('頭像上傳失敗');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return <LoadingPage message="加載中..." />;
  }

  return (
    <div className="member-page">
      <div className="page-header">
        <h1>個人資料</h1>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="btn btn-primary">
            編輯資料
          </button>
        ) : null}
      </div>

      <form onSubmit={handleSubmit} className="member-card">
        {/* Member Info */}
        {profile?.profile && (
          <div className="info-section">
            <h2>會員資訊</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>會員編號</label>
                <span className="info-value">{profile.profile.member_number}</span>
              </div>
              <div className="info-item">
                <label>加入日期</label>
                <span className="info-value">
                  {profile.profile.membership_date
                    ? formatDate(profile.profile.membership_date)
                    : formatDate(profile.profile.created_at)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="form-section">
          <h2>基本資料</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>中文姓名 *</label>
              <input
                type="text"
                name="chinese_name"
                value={formData.chinese_name}
                onChange={handleChange}
                disabled={!editing}
                required
              />
            </div>
            
            <div className="form-group">
              <label>英文姓名</label>
              <input
                type="text"
                name="english_name"
                value={formData.english_name}
                onChange={handleChange}
                disabled={!editing}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>電郵地址 *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!editing}
                required
              />
            </div>
            
            <div className="form-group">
              <label>電話號碼 *</label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                disabled={!editing}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>性別</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                disabled={!editing}
              >
                <option value="">請選擇</option>
                <option value="M">男</option>
                <option value="F">女</option>
                <option value="O">其他</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>興趣</label>
              <textarea name="interests" value={formData.interests || ''} onChange={handleChange} disabled={!editing} />
            </div>
            <div className="form-group">
              <label>緊急聯絡人</label>
              <input name="emergency_contact_name" value={formData.emergency_contact_name || ''} onChange={handleChange} disabled={!editing} />
            </div>
            <div className="form-group">
              <label>緊急聯絡電話</label>
              <input name="emergency_contact_phone" value={formData.emergency_contact_phone || ''} onChange={handleChange} disabled={!editing} />
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="form-section">
          <h2>地址資料</h2>
          
          <div className="form-group">
            <label>地址</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              disabled={!editing}
              placeholder="例如：香港島中西區皇后大道中99號"
            />
          </div>
          
          <div className="form-group">
            <label>所屬地區</label>
            <select
              name="district"
              value={formData.district}
              onChange={handleChange}
              disabled={!editing}
            >
              <option value="">請選擇</option>
              <option value="中西區">中西區</option>
              <option value="灣仔區">灣仔區</option>
              <option value="東區">東區</option>
              <option value="南區">南區</option>
              <option value="油尖旺區">油尖旺區</option>
              <option value="深水埗區">深水埗區</option>
              <option value="九龍城區">九龍城區</option>
              <option value="黃大仙區">黃大仙區</option>
              <option value="觀塘區">觀塘區</option>
              <option value="葵青區">葵青區</option>
              <option value="荃灣區">荃灣區</option>
              <option value="屯門區">屯門區</option>
              <option value="元朗區">元朗區</option>
              <option value="北區">北區</option>
              <option value="大埔區">大埔區</option>
              <option value="沙田區">沙田區</option>
              <option value="西貢區">西貢區</option>
              <option value="離島區">離島區</option>
            </select>
          </div>
        </div>

        {editing && (
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '保存中...' : '保存更改'}
            </button>
            <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={saving}>
              取消
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default Profile;
